import styles from "./components.module.scss";
import React, { FC, useMemo, useState, useEffect } from "react";
import ShiftDescriptionContainer from "./ShiftDescriptionContainer";
import { exitReact } from "../../../../utils";
import { JOB_STATUSES } from "../../../dentist/PostJobPage/PostJob.utils";
import { usePersistedState } from "../../../../hooks";
import { HygienistProps, JobProps } from "utils/types";
import { entities } from "../../../../reduxStore";
import FixedLoader from "../../../../components/FixedLoader/FixedLoader";
import useActionApi from "../../../../hooks/useActionApi";
import Api from "../../../../api";
import dayjs from "dayjs";
import Popup from "../../../../components/Popup";
import { PARTIAL_OFFER_STATUS } from "../AvailableJobPage";
import { ContainedButton, OutlinedButton, Description } from "../AvailableJob.styles";
import MaxBookedPopup from "./MaxBookedPopup";
import { Analytics } from "utils/analytics";

// @ts-ignore
const TempStars = window.TempStars

interface MakeAnInstabookOfferProps {
    job: JobProps,
    onClose: () => void
    onSubmit: (rate: number) => void
}

const MakeAnInstabookOfferPopup: FC<MakeAnInstabookOfferProps> = ({ job, onClose, onSubmit }) => {
    const from = job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedStart;
    const to = job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedEnd;

    return (
        <div className={styles.popupContainer}>
            <div className={styles.titleContainer}>
                <h2 className={styles.popupTitle}>Instantly Book This Shift?</h2>
                <p className={styles.popupSubtitle}>
                    Important! Read Carefully
                </p>
            </div>
            <div className={styles.contentContainer}>
                <div className={styles.modalDescription}>
                    <span className={styles.emphasizedText}>You're about to book this shift.</span>
                </div>
                <div className={styles.modalDescription}>
                    <span className={styles.emphasizedText}>This is not like sending an offer</span> - you will be instantly booked and confirmed for this shift. The office and their patients will be counting on you to keep your commitment.
                </div>
                <div className={styles.modalDescription}>
                    <span className={styles.emphasizedText}>Review the details carefully</span> and click "Book This Shift Now" if you agree to commit to this shift, otherwise click "Cancel".
                </div>
                <div className={styles.shiftDescriptionWrapper}>
                    <ShiftDescriptionContainer
                        header={job.location.practiceName}
                        address={job.location.address}
                        from={from}
                        to={to}
                        rate={job.ghr || job.proposedRate}
                        isConfirmModal={true}
                    />
                </div>
            </div>

            <ContainedButton
                className={styles.modalButton}
                variant="contained"
                size="large"
                onClick={() => {
                    onSubmit(job.proposedRate)
                    onClose()
                }}          >
                Book This Shift Now
            </ContainedButton>
            <OutlinedButton
                className={styles.modalButton}
                variant="outlined"
                size="large"
                onClick={onClose}
            >
                Cancel
            </OutlinedButton>
        </div>
    );
}


const InstaBookOfferModal: FC<{ job: JobProps, history: any, trackData: any, isFavourite: boolean }> = (
    {
        job,
        history,
        trackData,
        isFavourite
    }) => {
    const [isOpenMakeInstabookOfferModal, setIsOpenMakeInstabookOfferModal] = useState<boolean>(false);
    const [isOpenConfirmInstabookOfferModal, setIsOpenConfirmInstabookOfferModal] = useState<boolean>(false);
    const hygienist = usePersistedState<HygienistProps>({
        entityName: entities.hygienist
    })[0];
    const [isMaxBookedPopupOpen, setIsMaxBookedPopupOpen] = useState<boolean>(false);
    const [hygienistStatus, setHygienistStatus] = useState<number>(0);
    const [completedShifts, setCompletedShifts] = useState<number>(0);

    useEffect(() => {
        const fetchHygienistData = async () => {
            setHygienistStatus(hygienist?.status);
            setCompletedShifts(hygienist?.completedJobs);
        };
        fetchHygienistData();
    }, []);

    const [makeInstabookOfferLoading, , makeInstabookOffer] = useActionApi({
        apiMethod: Api.makeInstabookOffer,
        onSuccess: ({ offerId }: { offerId: number }) => {
            offerId && confirmInstabook({ offerId, jobId: job.id })
            setIsOpenMakeInstabookOfferModal(false)
        },
        onError: (e) => {
            console.log('error', JSON.parse(e.message))
            setIsOpenMakeInstabookOfferModal(false)
        }
    });

    const [confirmInstabookLoading, , confirmInstabook] = useActionApi({
        apiMethod: Api.confirmInstabook,
        onSuccess: () => {
            Analytics.track('instabook shift', trackData);
            setIsOpenConfirmInstabookOfferModal(true)
        },
        onError: (e) => {
            setIsOpenConfirmInstabookOfferModal(false)
            console.error('error confirm offer', e)
        }
    });

    const [revertInstabookLoading, , revertInstabook] = useActionApi({
        apiMethod: Api.revertInstabook,
        onSuccess: () => {
            setIsOpenConfirmInstabookOfferModal(false)
            Analytics.track('instabook shift', trackData);
            history.push('loading')
            exitReact('home')
        },
    });

    const onGoHome = () => {
        setIsOpenConfirmInstabookOfferModal(false)
        history.push('loading')
        exitReact('home')
    }

    const oldOfferId = useMemo(() => {
        return job?.partialOffers?.find((offer: any) => offer.hygienistId === hygienist.id
            && offer.status === PARTIAL_OFFER_STATUS.REMOVED)?.id
    }, [job])
    
    const isShowInstabookBtn = (job.status === JOB_STATUSES.POSTED || job.status === JOB_STATUSES.PARTIAL || (JOB_STATUSES.OFFER_EXPIRED === job.status && oldOfferId))
        && job.Instabook && (hygienist.status > 0 || isFavourite)

    const isAvailableBooking = () => {
        return (TempStars.Hygienist.getBookedShiftsNumber() < hygienist.maxBooked);
    };

    const onMakeInstabookOffer = async (rate: number) => {
        if (!isAvailableBooking()) {
            setIsMaxBookedPopupOpen(true);
            return;
        }

        const offeredStartTime = dayjs(job.startDate + ' ' + job.shifts[0].postedStart, 'YYYY-MM-DD h:mm a').format('YYYY-MM-DD HH:mm')
        const offeredEndTime = dayjs(job.startDate + ' ' + job.shifts[0].postedEnd, 'YYYY-MM-DD h:mm a').format('YYYY-MM-DD HH:mm')

        Analytics.track('Instabook_Confirmed', {});

        await makeInstabookOffer({
            hourlyRate: rate,
            offeredStartTime,
            offeredEndTime,
            jobId: job.id,
            startDate: job.startDate,
            available: true,
        }).then(() => {
            setIsOpenMakeInstabookOfferModal(false);
            history.push('loading');
            exitReact('home');
        })
    }

    const InstabookShiftPopup = () => {
        const from = job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedStart;
        const to = job.startDate.replace(/-/g, "/") + ' ' + job.shifts[0].postedEnd;
        return (
            <div className={styles.popupContainer}>
                <div className={styles.titleContainer}>
                    <h2 className={styles.popupTitle__confirm}>Nicely Done! You're all set.</h2>
                    <p className={styles.popupSubtitle}>
                        Important! Read Carefully
                    </p>
                </div>
                <div className={styles.contentContainer}>
                    <div className={styles.modalDescription}>
                        <span className={styles.emphasizedText}>You're booked and confirmed for your shift. {job.location.practiceName} and their patients are now counting on you.</span>
                    </div>
                    <div className={styles.modalDescription}>
                        We sent you a confirmation email with all the details, make sure you received it and review carefully.
                    </div>
                    <div className={styles.shiftDescriptionWrapper}>
                        <ShiftDescriptionContainer
                            header={job.location.practiceName}
                            address={job.location.address}
                            from={from}
                            to={to}
                            rate={job.ghr || job.proposedRate}
                            isConfirmModal={true}
                        />
                    </div>
                </div>

                <ContainedButton
                    className={styles.modalButton}
                    variant="contained"
                    size="large"
                    onClick={onGoHome}
                >
                    Ok
                </ContainedButton>
            </div>
        );
    }

    const instabookHandler = () => {
        Analytics.track('Instabook_Button_Clicked', {});
        if (isAvailableBooking()) {
            setIsOpenMakeInstabookOfferModal(true);
        } else {
            setIsMaxBookedPopupOpen(true);
        }
    }

    return (isShowInstabookBtn && !job.Autobook ? <>
        <Popup opened={isOpenMakeInstabookOfferModal} onClose={() => setIsOpenMakeInstabookOfferModal(false)}>
            <MakeAnInstabookOfferPopup
                job={job}
                onClose={() => setIsOpenMakeInstabookOfferModal(false)}
                onSubmit={onMakeInstabookOffer}
            />
        </Popup>

        <Popup opened={isOpenConfirmInstabookOfferModal} onClose={() => setIsOpenConfirmInstabookOfferModal(false)}>
            <InstabookShiftPopup />
        </Popup>
        <MaxBookedPopup
            isOpen={isMaxBookedPopupOpen}
            onClose={() => setIsMaxBookedPopupOpen(false)}
            hygienistStatus={hygienistStatus}
            completedShifts={completedShifts}
        />

        <Description>As a benefit of being a {hygienist.status === 1 ? 'Pro' : (hygienist.status === 2 ? 'Elite' : null)} member, you can instantly book this shift without waiting!</Description>

        <ContainedButton
            variant="contained"
            size="large"
            onClick={instabookHandler}
        >
            Instantly Book This Shift @ ${job.ghr || job.proposedRate}/hr
        </ContainedButton>

        <Description>
            <span className={styles.emphasizedText}>OR</span>
        </Description>

        <FixedLoader
            loading={makeInstabookOfferLoading || confirmInstabookLoading || revertInstabookLoading}
        />
    </> : null)
}

export default InstaBookOfferModal;